import { useEffect, useRef } from 'react'
import { dimensions, STUDENT_WELFARE_CARD_INDEX } from '../data/homeDimensions'
import {
  clamp,
  computeCardValues,
  computeHeroValues,
  computeHeroMorphValues,
  measureImageRectRelativeToStage,
} from '../utils/scrollAnimation'

const IDLE_MS = 250

export function useHomeScrollAnimation(trackRef, layerRefs, enabled, stageScaleRef) {
  const progressRef = useRef(0)
  const rafRef = useRef(null)
  const idleTimerRef = useRef(null)

  useEffect(() => {
    if (!enabled) return undefined

    const applyFrame = (progress) => {
      const layers = layerRefs.current
      const values = computeHeroValues(progress)
      const stage = layers.stage
      const stageW = stage?.clientWidth ?? window.innerWidth
      const stageH = stage?.clientHeight ?? window.innerHeight
      const stageScale = stageScaleRef?.current ?? 1
      const swDim = dimensions[STUDENT_WELFARE_CARD_INDEX]
      const swCardImage = layers.cardImages?.[STUDENT_WELFARE_CARD_INDEX]
      const measuredImageRect =
        swCardImage && stage && values.morphPhase > 0
          ? measureImageRectRelativeToStage(swCardImage, stage)
          : null
      const morph = computeHeroMorphValues(
        values.morphPhase,
        stageW,
        stageH,
        stageScale,
        swDim,
        measuredImageRect,
      )

      if (layers.darkOverlay) layers.darkOverlay.style.opacity = String(values.darkOverlayOpacity)
      if (layers.heroGradient) layers.heroGradient.style.opacity = String(values.heroGradientOpacity)

      if (layers.heroImage) {
        if (morph.useFullBleed) {
          layers.heroImage.style.inset = '0'
          layers.heroImage.style.left = ''
          layers.heroImage.style.top = ''
          layers.heroImage.style.width = ''
          layers.heroImage.style.height = ''
          layers.heroImage.style.transform = 'translate3d(0, 0, 0) scale(1)'
        } else {
          layers.heroImage.style.inset = 'auto'
          layers.heroImage.style.left = `${morph.left}px`
          layers.heroImage.style.top = `${morph.top}px`
          layers.heroImage.style.width = `${morph.width}px`
          layers.heroImage.style.height = `${morph.height}px`
          layers.heroImage.style.transform = 'none'
        }

        layers.heroImage.style.opacity = String(morph.flyingOpacity)
        layers.heroImage.style.zIndex = values.morphPhase > 0 && !morph.morphComplete ? '6' : '2'
        layers.heroImage.style.border = 'none'
        layers.heroImage.style.boxShadow = 'none'
        layers.heroImage.style.pointerEvents = morph.morphComplete ? 'none' : 'auto'

        const showImageCardBg =
          !morph.useFullBleed && morph.cardChromeOpacity > 0 && !morph.morphComplete
        const imageCardBgAlpha = showImageCardBg
          ? morph.cardChromeOpacity * values.dimensionsOpacity
          : 0
        layers.heroImage.style.backgroundColor =
          imageCardBgAlpha > 0 ? `rgba(255, 254, 250, ${imageCardBgAlpha})` : 'transparent'
      }

      if (layers.heroImageClip) {
        layers.heroImageClip.style.borderRadius = `${morph.borderRadius}px`
      }

      if (layers.heroImgHome) {
        layers.heroImgHome.style.opacity = String(morph.heroHomeOpacity)
      }

      if (layers.heroImgMorph) {
        layers.heroImgMorph.style.opacity = String(morph.heroMorphOpacity)
      }

      if (swCardImage) {
        swCardImage.style.opacity = String(morph.cardImageOpacity)
      }

      if (layers.heroText) {
        layers.heroText.style.opacity = String(values.heroTextOpacity)
        layers.heroText.style.transform = `translate3d(0, ${values.heroTextY}px, 0)`
        layers.heroText.style.pointerEvents = values.heroTextOpacity > 0.2 ? 'auto' : 'none'
      }

      if (layers.dimensionsStage) {
        layers.dimensionsStage.style.opacity = String(values.dimensionsOpacity)
        layers.dimensionsStage.style.pointerEvents = values.cardsInteractive ? 'auto' : 'none'
      }

      if (layers.title) layers.title.style.opacity = String(values.titleOpacity)

      layers.cardWrappers?.forEach((wrapper, index) => {
        if (!wrapper) return

        const dim = dimensions[index]
        const rotator = layers.cardRotators?.[index]

        if (index === STUDENT_WELFARE_CARD_INDEX) {
          const morphDone = morph.morphComplete || values.cardsPhase > 0
          const swImageContainer = layers.cardImageContainers?.[STUDENT_WELFARE_CARD_INDEX]
          const showHeader = morphDone || morph.cardChromeOpacity > 0

          wrapper.style.opacity = morphDone ? '1' : String(morph.cardChromeOpacity)
          wrapper.style.transform = 'translate3d(0, 0, 0) scale(1)'

          if (morphDone) {
            wrapper.style.clipPath = ''
          } else if (showHeader && swImageContainer) {
            const wrapperRect = wrapper.getBoundingClientRect()
            const containerRect = swImageContainer.getBoundingClientRect()
            const imageTopPx = containerRect.top - wrapperRect.top
            const cardHeight = wrapperRect.height

            if (cardHeight > 0) {
              const clipBottomPct = ((cardHeight - imageTopPx) / cardHeight) * 100
              wrapper.style.clipPath = `inset(0 0 ${clipBottomPct}% 0)`
            }
          } else {
            wrapper.style.clipPath = ''
          }

          if (rotator) {
            rotator.style.transform = `rotate(${dim.rotate}deg)`
          }
          return
        }

        const cardValues = computeCardValues(values.cardsPhase, index, dimensions.length, dim)

        wrapper.style.opacity = String(cardValues.opacity)
        wrapper.style.transform = `translate3d(${cardValues.x}px, ${cardValues.y}px, 0) scale(${cardValues.scale})`

        if (rotator) {
          rotator.style.transform = `rotate(${dim.rotate + cardValues.extraRotate}deg)`
        } else {
          wrapper.style.transform = `translate3d(${cardValues.x}px, ${cardValues.y}px, 0) scale(${cardValues.scale}) rotate(${dim.rotate + cardValues.extraRotate}deg)`
        }
      })
    }

    const readProgress = () => {
      const track = trackRef.current
      if (!track) return 0

      const rect = track.getBoundingClientRect()
      const scrollable = track.offsetHeight - window.innerHeight
      if (scrollable <= 0) return 1

      return clamp(-rect.top / scrollable)
    }

    const tick = () => {
      const next = readProgress()

      if (Math.abs(next - progressRef.current) > 0.00005) {
        progressRef.current = next
        applyFrame(next)
      }

      rafRef.current = window.requestAnimationFrame(tick)
    }

    const stopLoop = () => {
      if (rafRef.current !== null) {
        window.cancelAnimationFrame(rafRef.current)
        rafRef.current = null
      }
    }

    const startLoop = () => {
      if (rafRef.current !== null) return
      rafRef.current = window.requestAnimationFrame(tick)
    }

    const scheduleIdleStop = () => {
      if (idleTimerRef.current !== null) {
        window.clearTimeout(idleTimerRef.current)
      }

      idleTimerRef.current = window.setTimeout(() => {
        idleTimerRef.current = null
        stopLoop()
      }, IDLE_MS)
    }

    const onScroll = () => {
      startLoop()
      scheduleIdleStop()
    }

    const onResize = () => {
      progressRef.current = readProgress()
      applyFrame(progressRef.current)
    }

    progressRef.current = readProgress()
    applyFrame(progressRef.current)
    startLoop()

    window.addEventListener('scroll', onScroll, { passive: true })
    window.addEventListener('resize', onResize)

    return () => {
      stopLoop()
      if (idleTimerRef.current !== null) window.clearTimeout(idleTimerRef.current)
      window.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [enabled, trackRef, layerRefs, stageScaleRef])
}
